import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import {
  HEDGED_COLOR_HEX,
  UNHEDGED_COLOR_HEX,
  VENUE_WARN_COLOR_HEX
} from './descalceColors';
import { buildDescalceDeepLink } from './descalceDeepLink';
import { residualIsReal } from './descalceResidual';
import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** An own-property check, not a `?? fallback`: a plain object lookup walks
 *  `Object.prototype`, so a payload value of `toString` would return a
 *  FUNCTION and slip past a nullish check into the template string. */
function resolvingVenueTitle(
  venue: NonNullable<OpenerDescalceRow['venue']>['resolvingVenue']
): string {
  return venue != null &&
    Object.prototype.hasOwnProperty.call(RESOLVING_VENUE_TITLE, venue)
    ? RESOLVING_VENUE_TITLE[venue]
    : RESOLVING_VENUE_TITLE.unknown;
}

const NEUTRAL_COLOR = 'grey.500';

/**
 * Kalshi settles first and Polymarket lags, so naming the venue tells the
 * operator where the resolved side is. Each string states ONLY what
 * `resolvingVenue` actually carries — it says nothing about the other leg's
 * state, which the snapshot does not report. `unknown` covers both a snapshot
 * published before the backend carried the field and any value outside the
 * union (the payload is an unvalidated cast, so the union is a claim).
 */
const RESOLVING_VENUE_TITLE: Record<
  'kalshi' | 'polymarket' | 'both' | 'unknown',
  string
> = {
  kalshi: 'RESOLVED ON KALSHI: that leg already settled',
  polymarket: 'RESOLVED ON POLYMARKET: that leg is redeemable',
  both: 'RESOLVED ON BOTH VENUES',
  unknown: 'RESOLVING: a leg already resolved at its venue'
};

/** Says only what `resolvingVenue` carries. It must NOT conclude "not a naked
 *  leg": the backend reconstructs a settled Kalshi leg from its settlement
 *  net, so a row that still shows a descalce here holds a real residual. */
const RESOLVING_SUFFIX =
  ' — that side is cash now and can no longer be traded against; any descalce above is still real.';

/** For a snapshot with no `resolvingVenue`: the backend did not fold the
 *  resolved leg back in, so the number above is a resolution artefact. */
const LEGACY_RESOLVING_SUFFIX =
  ' — a resolution artefact pending redemption, not a naked leg to hedge.';

const INCOMPLETE_TITLE =
  'INCOMPLETE MARKET: this rule is missing a ticker or a Polymarket leg, or its two Polymarket tokens collide — in which case the Poly counts are SUPPRESSED, not zero. Either way the number above is not a true cross-venue descalce.';

/**
 * `Descalce` column cell — the sort key. Grey `—` when the row's account is
 * unreadable or the market is claimed by more than one rule (`ambiguous`, an
 * account-wide read that belongs to no single row); red beyond the
 * `IMBALANCE_TOLERANCE` even when `resolving`; amber for a resolved leg
 * within tolerance; green otherwise.
 */
export const DescalceCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.venue) {
    return (
      <Box component="span" data-descalce-state="unavailable" sx={{ color: NEUTRAL_COLOR }}>
        —
      </Box>
    );
  }
  if (row.venue.ambiguous) {
    return (
      <Tooltip
        title="More than one rule claims this market — this venue read is account-wide and belongs to no single row."
        placement="top"
      >
        <Box
          component="span"
          data-descalce-state="ambiguous"
          sx={{ color: NEUTRAL_COLOR, borderBottom: `1px dotted ${NEUTRAL_COLOR}` }}
        >
          —
        </Box>
      </Tooltip>
    );
  }
  const value = row.venue.descalce;
  const resolving = row.venue.resolving;
  const incomplete = row.venue.marketsIncomplete === true;
  // Magnitude decides, but only where the residual is trustworthy: red on an
  // untrustworthy number sends the operator hedging a position that is only
  // awaiting redemption. `incomplete` merely blocks GREEN — "hedged" is a
  // claim one-venue counts cannot support — without downgrading a real
  // one-venue naked leg to amber, which reads as "expected transient" in
  // `live/NetCell`.
  const trustworthy = residualIsReal(row.venue);
  const color =
    value > IMBALANCE_TOLERANCE && trustworthy
      ? UNHEDGED_COLOR_HEX
      : resolving || incomplete
        ? VENUE_WARN_COLOR_HEX
        : HEDGED_COLOR_HEX;
  // `incomplete` is reported even when the row also resolved: "one venue only"
  // is the stronger caveat of the two, and suppressing it would tell the
  // operator to go redeem a Polymarket leg that this rule does not have.
  const resolvingTitle = resolving
    ? `${resolvingVenueTitle(row.venue.resolvingVenue)}${
        trustworthy ? RESOLVING_SUFFIX : LEGACY_RESOLVING_SUFFIX
      }`
    : null;
  const title =
    [resolvingTitle, incomplete ? INCOMPLETE_TITLE : null]
      .filter(Boolean)
      .join(' · ') || 'Leg imbalance in contracts, account-wide for this market.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-descalce-state={resolving ? 'resolving' : 'naked'}
        data-descalce-incomplete={incomplete ? 'true' : undefined}
        sx={{
          color,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          borderBottom: incomplete ? `1px dotted ${color}` : undefined
        }}
      >
        {fmt(value)}
      </Box>
    </Tooltip>
  );
};

/** `YES +N` / `NO +N` from the sign of `venue.signedNet`. */
export const SideCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.venue || row.venue.ambiguous) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        —
      </Typography>
    );
  }
  const net = row.venue.signedNet;
  if (net === 0) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        flat
      </Typography>
    );
  }
  const label = net > 0 ? `YES +${fmt(net)}` : `NO +${fmt(Math.abs(net))}`;
  return (
    <Typography noWrap title={label} sx={{ fontSize: '0.75rem' }}>
      {label}
    </Typography>
  );
};

/**
 * Chip label/color for a row's `group.podsDesired`. `panicked` is kept
 * distinct from a benign `off` — mirrors the three-way mapping in
 * `entry/groupPodStatus.ts` (`GROUP_POD_STATUS_LABELS` /
 * `GROUP_POD_STATUS_CHIP_COLORS`), since a hard-panicked group is
 * plausibly the very cause of the naked leg this screen exists to surface
 * and must not read the same as a quietly-paused one.
 */
const GROUP_PODS_CHIP: Record<'on' | 'off' | 'panicked', { label: string; color: 'success' | 'default' | 'error' }> = {
  on: { label: 'ON', color: 'success' },
  off: { label: 'OFF', color: 'default' },
  panicked: { label: 'PANICKED', color: 'error' }
};

/** Group name + `ON`/`OFF`/`PANICKED` pod chip + `TEST` chip. A stopped
 *  group's naked leg must still be listed — this cell only marks it, never
 *  hides it. */
export const GroupCell = ({ group }: { group: OpenerDescalceRow['group'] }) => {
  const podsChip = GROUP_PODS_CHIP[group.podsDesired ?? 'off'] ?? GROUP_PODS_CHIP.off;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        title={group.name ?? 'Ungrouped'}
        sx={{ fontSize: '0.78rem', minWidth: 0 }}
      >
        {group.name ?? 'Ungrouped'}
      </Typography>
      {group.id && (
        <Chip
          label={podsChip.label}
          size="small"
          color={podsChip.color}
          data-group-pods={group.podsDesired ?? 'unknown'}
          sx={{ fontSize: '0.6rem', height: 16, flexShrink: 0 }}
        />
      )}
      {group.testAccount && (
        <Chip
          label="TEST"
          size="small"
          color="warning"
          sx={{ fontSize: '0.6rem', height: 16, fontWeight: 700, flexShrink: 0 }}
        />
      )}
    </Stack>
  );
};

/** Poly / Kalshi per-side contract count cell — plain number or `—`. */
export const VenueCountCell = ({ value }: { value: number | null | undefined }) => (
  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>
    {value == null ? '—' : fmt(value)}
  </Box>
);

export const BeliefCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.belief) {
    return (
      <Tooltip title="No BeliefSnapshot for this rule yet (engine off / restarted).">
        <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>
      </Tooltip>
    );
  }
  return (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>
      {fmt(row.belief.descalce)}
    </Box>
  );
};

/** `descalce − belief.descalce`, marked when `|diff| > IMBALANCE_TOLERANCE`. */
export const DiffCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.divergence) {
    return <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>;
  }
  const diff = row.divergence.total;
  const flagged = Math.abs(diff) > IMBALANCE_TOLERANCE;
  const title = flagged
    ? 'The engine belief disagrees with the venue read — either a fill the engine never booked, or, on a row whose resolved leg was capped, the naked size that went into settlement.'
    : 'Venue descalce and engine belief agree within tolerance.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-diff-flagged={flagged}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: flagged ? 700 : 400,
          color: flagged ? UNHEDGED_COLOR_HEX : 'inherit'
        }}
      >
        {diff > 0 ? '+' : ''}
        {fmt(diff)}
      </Box>
    </Tooltip>
  );
};

/** Rule status chip + `hedgeCheckEnabled` shield. */
export const RuleCell = ({ row }: { row: OpenerDescalceRow }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
    <Chip
      label={row.ruleStatus}
      size="small"
      title={row.ruleStatus}
      color={row.ruleStatus === 'active' ? 'success' : 'default'}
      sx={{
        fontSize: '0.65rem',
        height: 18,
        textTransform: 'capitalize',
        minWidth: 0,
        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
      }}
    />
    {row.hedgeCheckEnabled && (
      <Tooltip
        title={`hedge-watchdog enabled${
          row.maxUnhedgedContracts != null
            ? ` — max ${row.maxUnhedgedContracts} unhedged contracts`
            : ''
        }`}
      >
        <ShieldOutlinedIcon
          sx={{ fontSize: '0.9rem', color: HEDGED_COLOR_HEX, flexShrink: 0 }}
        />
      </Tooltip>
    )}
  </Stack>
);

/** `Go` deep-link to `/predictions/opening?group={id}&subgroup={sid}`,
 *  opened in a new tab. Disabled with a tooltip when the row is ungrouped. */
export const GoCell = ({ row }: { row: OpenerDescalceRow }) => {
  const href = buildDescalceDeepLink(row);
  if (!href) {
    return (
      <Tooltip title="Ungrouped rule (core engine) — no group page to open.">
        <span>
          <IconButton size="small" disabled data-go-disabled="true">
            <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Open this group / subgroup in the opener, in a new tab.">
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-go-href={href}
      >
        <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
      </IconButton>
    </Tooltip>
  );
};
